import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  Typography,
} from "@mui/material";
import Web3 from "web3";
import * as yup from "yup";
import { Form, Formik } from "formik";
import * as Sentry from "@sentry/nextjs";
import isNil from "lodash/isNil";
import detectEthereumProvider from "@metamask/detect-provider";
import { CtaButton } from "./CtaButton";
import useSignIdentity from "../api/useSignIdentity";
import useAuthRequest from "../api/useAuthRequest";
import useSignMintRequest from "../api/useSignMintRequest";
import { IWalletState, useWallet } from "./UseWallet";
// @ts-ignore
import { getBackendErrorMessage } from "../lib/backendError";
import contract from "abi/NFTAccessPass.json";
import { ButtonIcon } from "./ButtonIcon";
import {
  ErrorType,
  getAuthToken,
  getErrorType,
  hasSufficientFunds,
  isPriceMismatch,
  performMint,
  prepareMintRequestWithRetry,
} from "../lib/mint";
import ENV from "../lib/env";
import { TOTAL_PUBLIC_SUPPLY } from "../constants/mintConstants";
import styles from "../styles/MintForm.module.css";
import {
  ErrorState,
  MintingWindowState,
  MintWindowParams,
} from "../types/mint";
import { BigNumber } from "ethers";
import { formatUnits } from "ethers/lib/utils";
import AppContext from "./AppContext";
import { useRouter } from "next/router";
import RightArrowIcon from "./RightArrowIcon";
import ToggleButtons from "./ToggleButtons";
import MintErrorModal from "./MintErrorModal";
import DiscordIcon from "./DiscordIcon";

const validationSchema = yup.object({
  amount: yup.number().required("Select the number of NFTs you wish to mint."),
  agree: yup
    .bool()
    .oneOf(
      [true],
      "Please agree to the terms and conditions in order to mint."
    ),
});

interface Props {
  setAlertState: any;
  closeAlert: any;
  mintWindowParams: MintWindowParams;
  setNftsMinted: any;
  nftsMinted: number | null;
}

const mintPrice = (amountStr: string, basePrice: BigNumber): BigNumber => {
  const amount = Number.parseInt(amountStr);
  return basePrice.mul(BigNumber.from(amount));
};

const showErrorAlert = (
  setAlertState: any,
  closeAlert: any,
  errorMessage: string
) => {
  setAlertState({
    isOpen: true,
    closeAlert,
    message: errorMessage,
    severity: "error",
    handleClose: null,
    horizontal: "center",
    vertical: "top",
  });
};

const MintForm = ({
  setAlertState,
  closeAlert,
  mintWindowParams,
  setNftsMinted,
  nftsMinted,
}: Props) => {
  const context = useContext(AppContext);
  const router = useRouter();
  const signIdentity = useSignIdentity();
  const authRequest = useAuthRequest();
  const signMintRequest = useSignMintRequest();
  const [errorState, setErrorState] = useState<ErrorState>({
    type: null,
    message: null,
  } as ErrorState);
  const [provider, setProvider] = useState<any | null>(null);

  const detectProvider = useCallback(async () => {
    const provider = await detectEthereumProvider();
    if (provider) {
      setProvider(provider);
    } else {
      setAlertState({
        isOpen: true,
        closeAlert,
        message:
          "Unable to detect web3 wallet. Please reload the page or try a different browser with a web3 wallet.",
        severity: "error",
        handleClose: null,
        horizontal: "center",
        vertical: "top",
      });
    }
  }, [closeAlert, setAlertState]);

  useEffect(() => {
    detectProvider();
  }, []);

  const loadNftCount = async () => {
    const web3 = new Web3(ENV.WEB3_PROVIDER!);

    const tdhContract = new web3.eth.Contract(
      // @ts-ignore
      contract.abi,
      ENV.CONTRACT_ADDRESS!
    );
    const count = await tdhContract.methods.publicTokensMinted().call();
    setNftsMinted(Number.parseInt(count));
  };

  useEffect(() => {
    loadNftCount();
  }, []);

  const onWalletError = useCallback(
    (error) => {
      showErrorAlert(
        setAlertState,
        closeAlert,
        `Unable to connect to wallet: ${error}`
      );
    },
    [setAlertState, closeAlert]
  );

  // TODO
  const onNoWallet = useCallback(async () => {}, []);

  const onWalletConnect = useCallback(
    async ({ address, web3Provider }: IWalletState, values) => {
      if (isNil(address)) {
        return;
      }

      const price: BigNumber = mintPrice(
        values.amount,
        mintWindowParams.basePrice
      );
      const amount = Number.parseInt(values.amount);

      if (!(await hasSufficientFunds(web3Provider, price, provider))) {
        setErrorState({ type: ErrorType.INSUFFICIENT_FUNDS, message: null });
        return;
      }

      let mintRequest;

      const signer = web3Provider.getSigner();

      try {
        await getAuthToken(signIdentity, address, signer, authRequest);
      } catch (error) {
        console.log({ error });
        const errorType = getErrorType(error);
        console.log({ errorType });
        setErrorState(errorType);
        if (errorType.type === ErrorType.GENERIC_ERROR) {
          Sentry.captureException(error);
        }
        return;
      }

      try {
        mintRequest = await prepareMintRequestWithRetry(
          address,
          amount,
          signMintRequest,
          signIdentity,
          signer,
          authRequest
        );
      } catch (error) {
        console.log({ error });
        const errorType = getErrorType(error);
        setErrorState(errorType);
        if (errorType.type === ErrorType.GENERIC_ERROR) {
          Sentry.captureException(error);
        }
        return;
      }

      if (isPriceMismatch(mintRequest, mintWindowParams.basePrice, amount)) {
        setErrorState({ type: ErrorType.PRICE_MISMATCH, message: null });
        return;
      }

      try {
        setAlertState({
          isOpen: true,
          closeAlert,
          message:
            "Minting NFT(s). Please be patient as this can take several minutes to process.",
          severity: "info",
          handleClose: null,
          horizontal: "center",
          vertical: "top",
        });

        await performMint(
          contract,
          signer,
          setAlertState,
          mintRequest,
          closeAlert
        );
        // @ts-ignore
        context.setSession({ success: true });
        await router.push("mintSuccess");
      } catch (error) {
        console.log({ error });
        const errorType = getErrorType(error);
        setErrorState(errorType);
        if (errorType.type === ErrorType.GENERIC_ERROR) {
          Sentry.captureException(error);
        }
        // reset alert.
        setAlertState({
          isOpen: false,
          closeAlert,
          message: "",
          severity: "info",
          handleClose: null,
          horizontal: "center",
          vertical: "top",
        });
      }
    },
    [
      signIdentity,
      closeAlert,
      authRequest,
      signMintRequest,
      setAlertState,
      mintWindowParams,
      router,
      context,
      provider,
    ]
  );

  const { handleConnectWallet } = useWallet(
    onWalletConnect,
    onWalletError,
    onNoWallet
  );

  const onMintClick = useCallback(
    async (values) => {
      await handleConnectWallet(values);
    },
    [handleConnectWallet]
  );

  const handleCloseMintErrorModal = useCallback(() => {
    setErrorState({ type: null, message: null });
  }, [setErrorState]);

  const nftSupplyExhaused = useMemo(
    () => !isNil(nftsMinted) && nftsMinted >= TOTAL_PUBLIC_SUPPLY,
    [nftsMinted]
  );

  const mintButtonDisabled = useMemo(
    () =>
      !provider ||
      mintWindowParams.state === MintingWindowState.BEFORE_MINT ||
      nftSupplyExhaused,
    [provider, mintWindowParams, nftSupplyExhaused]
  );

  const merchMessage = (amountStr: string) => {
    const amount = Number.parseInt(amountStr);
    if (amount >= 2) {
      return (
        <Box
          sx={{
            background:
              "linear-gradient(20deg, #73358F 0%, #BC3852 35%, #ED5736 75%, #E6824B 100%);",
            fontWeight: 800,
            "-webkit-background-clip": "text",
            "-webkit-text-fill-color": "transparent",
            fontSize: "16px",
            color: "#000",
            textTransform: "uppercase",
            minHeight: "24px",
          }}
        >
          Includes merch pack
        </Box>
      );
    }

    return (
      <Box
        sx={{
          fontSize: "16px",
          fontWeight: 800,
          color: "#ccc",
          textTransform: "uppercase",
          minHeight: "24px",
        }}
      >
        No merch pack
      </Box>
    );
  };

  if (nftSupplyExhaused) {
    return (
      <Grid sx={{ background: "#000", padding: "20px", marginTop: "30px" }}>
        <Box
          sx={{
            background:
              "linear-gradient(20deg, #73358F 0%, #BC3852 35%, #ED5736 75%, #E6824B 100%);",
            fontWeight: 800,
            "-webkit-background-clip": "text",
            "-webkit-text-fill-color": "transparent",
            fontSize: "26px",
            color: "#000",
            minHeight: "24px",
          }}
        >
          Sold Out
        </Box>

        <Grid container className={styles.buttonsContainer}>
          <Grid item className={styles.buttonWrapper}>
            <a href="https://discord.gg/A7gVznZBJz">
              <CtaButton>
                <Box>Join Discord</Box>
                <Box>
                  <ButtonIcon icon={<DiscordIcon />} />
                </Box>
              </CtaButton>
            </a>
          </Grid>
          <Grid item className={styles.buttonWrapper}>
            <CtaButton>
              <Box>Join Email List</Box>
              <Box>
                <ButtonIcon icon={<RightArrowIcon />} />
              </Box>
            </CtaButton>
          </Grid>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container mt={5} direction="column" sx={{ alignItems: "center" }}>
      <Formik
        initialValues={{
          amount: "2",
          agree: false,
        }}
        validationSchema={validationSchema}
        onSubmit={onMintClick}
      >
        {({
          submitForm,
          isSubmitting,
          values,
          setFieldValue,
          errors,
          touched,
        }) => (
          <Box
            sx={{
              width: "100%",
              maxWidth: { xs: "350px", sm: "450px" },
              background: "#19181B",
              padding: "20px",
              borderRadius: "48px",
            }}
          >
            <Form>
              <Box mb={3}>
                <ToggleButtons
                  amount={values.amount}
                  setFieldValue={setFieldValue}
                />
              </Box>

              <Box mb={3}>
                <Typography sx={{ fontWeight: 800, fontSize: "20px" }}>
                  <Typography
                    sx={{
                      display: "inline",
                      verticalAlign: "-12px",
                      marginRight: "10px",
                    }}
                  >
                    <img src="/images/mint/eth.svg" alt="eth icon" />
                  </Typography>
                  {formatUnits(
                    mintPrice(values.amount, mintWindowParams.basePrice)
                  )}{" "}
                  ETH{" "}
                  <Typography sx={{ display: "inline" }}>+ gas fees</Typography>
                </Typography>
                {merchMessage(values.amount)}
              </Box>

              <CtaButton
                onClick={submitForm}
                loading={isSubmitting}
                disabled={mintButtonDisabled || isSubmitting}
              >
                <Box>MINT</Box>
                <Box>
                  <ButtonIcon
                    disabled={mintButtonDisabled}
                    loading={isSubmitting}
                    icon={<RightArrowIcon />}
                  />
                </Box>
              </CtaButton>
              <FormControlLabel
                sx={{ marginTop: "8px" }}
                control={
                  <Checkbox
                    sx={{ svg: { color: "#fff" } }}
                    name="agree"
                    onClick={() => setFieldValue("agree", !values.agree)}
                  />
                }
                label={
                  <div>
                    I agree to the{" "}
                    <a
                      href="https://www.thedarkhorse.xyz/termsandconditions"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Typography
                        sx={{ display: "inline", textDecoration: "underline" }}
                      >
                        Terms and Conditions
                      </Typography>
                    </a>
                  </div>
                }
              />

              {touched.agree && errors.agree && (
                <Box mt={2}>
                  <Alert severity="error">{errors.agree}</Alert>
                </Box>
              )}
            </Form>
          </Box>
        )}
      </Formik>
      <MintErrorModal
        errorState={errorState}
        handleClose={handleCloseMintErrorModal}
      />
    </Grid>
  );
};

export default MintForm;
